import { yupResolver } from '@hookform/resolvers/yup';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import 'react-day-picker/lib/style.css';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector, useStore } from 'react-redux';
import * as Yup from 'yup';
import { clearAmenity, getAmenityV2 } from '../../../redux/actions/amenities';
import {
  clearAmenityAvailabilityV2,
  clearBookingError,
  editBookingV3,
  getBookingsCalendarV3,
  getBookingsListV3
} from '../../../redux/actions/bookingParAm';
import { changeOpenedModal } from '../../../redux/actions/headerModal';
import { setToast } from '../../../redux/actions/window';
import { delay } from 'lodash';
import { useTranslation } from 'react-i18next';
import { WizardModalLayout } from '../../../components/ModalScrollProgress';
import {
  getBookingWizardModalProps,
  StyledModalCardV2
} from '../../../components/modalV2';
import LoadingAvatar from '../../../components/LoadingAvatar';
import { LOADING_BLUE } from '../../../utils/colorCode';
import { GET_AMENITY_AVAILABILITY_V2, GET_BOOKING_V3 } from '../../../redux/constants';
import { requestFail, requestPending, requestSuccess } from '../../../utils/status';
import AddBlockConfirmationStepV2 from './AddBlockConfirmationStepV2';
import AddBlockDetailStepV2 from './AddBlockDetailStepV2';
import { buildListV3Params } from './bookingsFilterHelpers';
import { useBookingAvailabilityFetcher } from '../../properties/Unit&Amenities/Amenities/V2/useBookingAvailabilityFetcher';
import {
  AVAILABILITY_QUERY_TYPE_BLOCK,
  BOOKING_FORM_NO_TIME,
  buildBlockEditFormDefaults,
  buildBlockEditPayload,
  buildBlockValidationSchema
} from './bookingFormHelpers';
import { useBlockDetailForm } from './useBlockDetailForm';
import {
  BOOKING_WIZARD_STEP_COUNT,
  getEditBlockWizardChromeProps
} from './bookingWizardConfigV2';
import WizardChromeV2 from '../../../components/WizardV2/WizardChromeV2';
import {
  WizardFooterButtonPrimary,
  WizardFooterButtonSecondary
} from '../../../components/WizardV2/wizardStylesV2';

const EditBlockV2 = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const store = useStore();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [isFormInitialized, setIsFormInitialized] = useState(false);

  const initializedBookingIdRef = useRef(null);

  const { booking, error, availabilitySlotsV2, status: bookingStatus } = useSelector(
    (state) => state.parAmBooking
  );
  const { modal } = useSelector((state) => state.headerModal);
  const { amenities, amenity } = useSelector((state) => state.amenities);

  const availabilitySlots = availabilitySlotsV2;
  const slotsLoading = bookingStatus === requestPending(GET_AMENITY_AVAILABILITY_V2);
  const bookingLoading = bookingStatus === requestPending(GET_BOOKING_V3);
  const bookingFailed = bookingStatus === requestFail(GET_BOOKING_V3);

  const showModal = modal === 'editBlockV2' || modal === 'editBlockV2Loading';
  const showForm = modal === 'editBlockV2' && booking?.id && !bookingLoading && !bookingFailed;

  const [verifyTimeslot, setVerifyTimeslot] = useState(false);

  const validationSchema = useMemo(
    () =>
      buildBlockValidationSchema({
        yup: Yup,
        t,
        amenity,
        availabilitySlots,
        verifyTimeslot
      }),
    [t, verifyTimeslot, availabilitySlots, amenity]
  );

  const schemaRef = useRef(validationSchema);
  schemaRef.current = validationSchema;

  const dynamicYupResolver = useCallback((values, context, options) => {
    return yupResolver(schemaRef.current)(values, context, options);
  }, []);

  const {
    control,
    handleSubmit,
    watch,
    trigger,
    getValues,
    reset,
    setValue,
    resetField,
    clearErrors,
    formState: { errors }
  } = useForm({
    resolver: dynamicYupResolver,
    mode: 'onChange',
    defaultValues: {
      startTime: BOOKING_FORM_NO_TIME,
      endTime: BOOKING_FORM_NO_TIME,
      endDate: undefined,
      reason: ''
    }
  });

  const watchAmenityId = watch('amenity_id');
  const watchStartDate = watch('startDate');
  const watchEndDate = watch('endDate');
  const watchStartTime = watch('startTime');
  const watchEndTime = watch('endTime');

  const { lastAvailabilityKeyRef } = useBookingAvailabilityFetcher({
    watchStartDate,
    watchAmenityId: booking?.amenity_id,
    bookingId: booking?.id,
    type: AVAILABILITY_QUERY_TYPE_BLOCK,
    dispatch,
    active: showForm,
    setVerifyTimeslot
  });

  const { startTimeOptions, endTimeOptions, disabledDates, disabledEndDate } = useBlockDetailForm({
    amenity,
    availabilitySlots,
    watchStartTime,
    watchEndDate,
    watchStartDate,
    watchEndTime,
    watchAmenityId,
    watchBuilding: null,
    setValue,
    clearErrors,
    isFormInitialized
  });

  useEffect(() => {
    if (!showForm || !booking?.id) return;
    if (initializedBookingIdRef.current === booking.id) return;

    const defaults = buildBlockEditFormDefaults(booking);
    if (!defaults) return;

    initializedBookingIdRef.current = booking.id;
    reset(defaults);
    setIsFormInitialized(true);
    lastAvailabilityKeyRef.current = null;
    setWizardStep(0);
    setVerifyTimeslot(false);
  }, [showForm, booking, reset]);

  useEffect(() => {
    if (!showModal) {
      setWizardStep(0);
      lastAvailabilityKeyRef.current = null;
      initializedBookingIdRef.current = null;
      setIsFormInitialized(false);
    }
  }, [showModal]);

  useEffect(() => {
    if (!showForm) return;
    if (watchStartDate) {
      trigger('startDate');
    }
  }, [showForm, watchStartDate, amenity, trigger]);

  useEffect(() => {
    if (
      bookingStatus === requestSuccess(GET_AMENITY_AVAILABILITY_V2) ||
      bookingStatus === requestFail(GET_AMENITY_AVAILABILITY_V2)
    ) {
      setVerifyTimeslot(true);
    }
  }, [bookingStatus]);

  useEffect(() => {
    const validateStartDate = async () => {
      await trigger(['startDate']);
    };
    if (watchStartDate && verifyTimeslot) {
      delay(validateStartDate, 500);
    }
  }, [availabilitySlots, verifyTimeslot, trigger, watchStartDate]);

  useEffect(() => {
    if (!showForm || !booking?.amenity_id) return;
    const amenityIdInt = parseInt(booking.amenity_id, 10);
    if (!Number.isFinite(amenityIdInt) || amenityIdInt <= 0) return;

    dispatch(
      getAmenityV2({
        id: amenityIdInt,
        fail: () => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Failed to get amenity!'),
              modal: 'errorToast'
            })
          );
        }
      })
    );
  }, [showForm, booking?.amenity_id, dispatch, t]);

  const onClose = () => {
    dispatch(clearAmenity());
    dispatch(clearAmenityAvailabilityV2());
    dispatch(changeOpenedModal({ modal: '' }));
    dispatch(clearBookingError());
    setWizardStep(0);
    lastAvailabilityKeyRef.current = null;
    initializedBookingIdRef.current = null;
    setIsFormInitialized(false);
  };

  const customSubmit = (values) => {
    if (!booking?.id) return;
    setIsSubmitting(true);

    dispatch(
      editBookingV3({
        id: booking.id,
        body: buildBlockEditPayload(values, booking),
        success: () => {
          const latest = store.getState();
          const calendarSnap = latest.parAmBooking.lastBookingsCalendarV3Params;
          const buildingId = latest.building.globalBuildingId;
          onClose();
          if (calendarSnap && Number(calendarSnap.building) === Number(buildingId)) {
            dispatch(getBookingsCalendarV3(calendarSnap));
          }
          dispatch(
            getBookingsListV3(
              buildListV3Params({
                buildingId,
                page: 1,
                search: latest.parAmBooking.search,
                orgId: latest.Organization.globalOrgId
              })
            )
          );
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Block has been successfully updated!', {
                defaultValue: 'Block has been successfully updated!'
              })
            })
          );
          setIsSubmitting(false);
        },
        fail: () => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Failed to update block!', {
                defaultValue: 'Failed to update block!'
              }),
              modal: 'errorToast'
            })
          );
          setIsSubmitting(false);
        }
      })
    );
  };

  const handleNext = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const fields = ['startDate', 'endDate', 'startTime', 'endTime'];
    const ok = await trigger(fields, { shouldFocus: true });
    if (ok) setWizardStep(1);
  };

  const onFormSubmit = (e) => {
    if (wizardStep !== 1) {
      e.preventDefault();
      return;
    }
    handleSubmit(customSubmit)(e);
  };

  const getFilteredAmenities = () => amenities;

  const safeStep = Math.min(Math.max(0, wizardStep), BOOKING_WIZARD_STEP_COUNT - 1);
  const modalTitle = t('Edit Block', { defaultValue: 'Edit Block' });
  const wizardChromeProps = getEditBlockWizardChromeProps(safeStep, t, { modalTitle });

  if (!showModal) {
    return null;
  }

  return (
    <form name="EditBlockForm" onSubmit={onFormSubmit}>
      <WizardModalLayout
        Modal={StyledModalCardV2}
        modalProps={getBookingWizardModalProps(safeStep === 1)}
        show={showModal}
        onClose={onClose}
        width="460px"
        bodyGap="12px"
        deps={[wizardStep, showForm, booking?.id]}
        error={error}
        footerActions={
          showForm ? (
            wizardStep === 0 ? (
              <>
                <WizardFooterButtonSecondary type="button" onClick={onClose}>
                  {t('Cancel')}
                </WizardFooterButtonSecondary>
                <WizardFooterButtonPrimary type="button" onClick={handleNext}>
                  {t('Next', { defaultValue: 'Next' })}
                </WizardFooterButtonPrimary>
              </>
            ) : (
              <>
                <WizardFooterButtonSecondary type="button" onClick={() => setWizardStep(0)}>
                  {t('Back')}
                </WizardFooterButtonSecondary>
                <WizardFooterButtonPrimary type="submit" disabled={isSubmitting}>
                  {t('Update Block', { defaultValue: 'Update Block' })}
                </WizardFooterButtonPrimary>
              </>
            )
          ) : null
        }>
        <WizardChromeV2 {...wizardChromeProps}>
          {bookingLoading || modal === 'editBlockV2Loading' ? (
            <LoadingAvatar color={LOADING_BLUE} />
          ) : bookingFailed ? (
            <div className="error_for_modal p-4">
              <h4 className="text-danger">{error}</h4>
            </div>
          ) : showForm ? (
            <>
              {wizardStep === 0 && (
                <AddBlockDetailStepV2
                  mode="edit"
                  fieldsLocked
                  t={t}
                  control={control}
                  errors={errors}
                  watch={watch}
                  resetField={resetField}
                  amenity={amenity}
                  disabledDates={disabledDates}
                  disabledEndDate={disabledEndDate}
                  getFilteredAmenities={getFilteredAmenities}
                  startTimeOptions={startTimeOptions}
                  endTimeOptions={endTimeOptions}
                  watchStartTime={watchStartTime}
                  slotsLoading={slotsLoading}
                  amenityDisplayName={
                    booking?.amenity_name ||
                    booking?.title ||
                    amenity?.description ||
                    amenities.find(
                      (a) => parseInt(a.id, 10) === parseInt(booking?.amenity_id, 10)
                    )?.description
                  }
                />
              )}

              {wizardStep === 1 && (
                <AddBlockConfirmationStepV2 t={t} getValues={getValues} amenities={amenities} />
              )}
            </>
          ) : null}
        </WizardChromeV2>
      </WizardModalLayout>
    </form>
  );
};

export default EditBlockV2;
